import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allArticles } from '@/mocks/articleData';
import { latestUpdates } from '@/mocks/homeData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleNumber = parseInt(id || '1');
  const article = allArticles[articleNumber] || allArticles[1];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const getCategorySlug = (cat: string) => {
    const map: Record<string, string> = {
      '热血': 'rexue', '恋爱': 'lianai', '奇幻': 'qihuan', '武侠': 'wuxia',
      '玄幻': 'xuanhuan', '悬疑': 'xuanyi', '搞笑': 'gaoxiao', '恐怖': 'kongbu',
      '校园': 'xiaoyuan', '穿越': 'chuanyue',
    };
    return map[cat] || 'rexue';
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ccbcht.com/' },
      { '@type': 'ListItem', position: 2, name: `${article.category}漫画`, item: 'http://ccbcht.com/category/all' },
      { '@type': 'ListItem', position: 3, name: article.title, item: `http://ccbcht.com/article/${article.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${article.title} - 韩漫在线`,
    description: article.description,
    author: { '@type': 'Person', name: article.author },
    datePublished: '2024-01-01',
    dateModified: article.chapters[0]?.updateDate || '2026-07-03',
    image: article.cover,
    publisher: { '@type': 'Organization', name: '韩漫在线', url: 'http://ccbcht.com/' },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-[1200px] mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/category/all" className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}漫画</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-800">{article.title}</span>
              </nav>

              <div className="flex flex-col md:flex-row gap-8 md:gap-12">
                <div className="md:w-[280px] flex-shrink-0">
                  <div className="rounded-xl overflow-hidden border border-background-200/70">
                    <img
                      src={article.cover}
                      alt={article.title}
                      className="w-full aspect-[3/4] object-cover object-top"
                      title={`${article.title} - 韩漫在线韩国漫画`}
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Link
                      to="/category/all"
                      className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium cursor-pointer"
                    >
                      {article.category}
                    </Link>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      article.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {article.status}
                    </span>
                  </div>

                  <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading">
                    {article.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <strong className="text-foreground-900">{article.rating}</strong>
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views} 次阅读
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-background-200 text-xs text-foreground-600">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <h2 className="text-sm font-semibold text-foreground-900 mb-2 font-heading">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{article.description}</p>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <button className="flex-1 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                      <i className="ri-play-line"></i>
                      开始阅读（第1话）
                    </button>
                    <button className="px-6 py-3 bg-background-100 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-200 transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                      <i className="ri-bookmark-line"></i>
                      加入收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-10">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-foreground-900 font-heading">章节目录</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-md bg-background-100 text-xs font-medium text-foreground-600 hover:bg-background-200 transition-all cursor-pointer">
                  正序
                </button>
                <button className="px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium cursor-pointer">
                  倒序
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {article.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between p-3.5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-foreground-900 font-heading">
                      第 {chapter.number} 话
                    </span>
                    <span className="text-xs text-foreground-600">{chapter.title}</span>
                    {chapter.isNew && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-background-50 bg-accent-500 whitespace-nowrap">
                        NEW
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 hidden sm:inline">{chapter.updateDate}</span>
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="text-lg font-bold text-foreground-900 mb-6 font-heading">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {latestUpdates.map((comic) => (
                <Link
                  key={comic.id}
                  to="/category/all"
                  className="group block cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={`${comic.title} - 韩漫在线推荐`}
                    />
                  </div>
                  <div className="mt-2">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate font-heading">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-foreground-500">{comic.category}</span>
                      <span className="text-xs text-accent-500 flex items-center gap-0.5">
                        <i className="ri-star-fill text-[10px]"></i>
                        {comic.rating}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}